// FIX3: booth_code から store_code を逆引き (PatrolBoothInputPage.deriveStoreCode と同ロジック)
function _deriveStoreCode(boothCd) {
  if (!boothCd) return null
  if (/^[A-Z]{3}\d{2}::/.test(boothCd)) return boothCd.split('::')[0]
  const p = boothCd.split('-')
  return p.length >= 2 ? p[0] : null
}

// SPEC-LF1-STORE-LOCAL-CACHE-01: 店舗離脱時の auto-sync オーケストレーション。
// - navigator.onLine + 軽量 connectivity probe で online 判定
// - 未送信 record を Supabase 既存 save 経路に渡して upload
// - 成功で markRecordSynced (LF1 は削除しない、synced=true へ flag のみ更新)
// - 失敗は ERR-XXX log + 未送信のまま、banner で表示継続

import { savePatrolReading, flagMeterDeltaAnomaly, flagPrizeOutAnomaly } from './patrolCore'
import { supabase } from '../lib/supabase'
import {
  getUnsyncedRecords,
  markRecordSynced,
  getPatrolRecordsByStore,
  putPatrolRecord,
  setRecordLastErrCode,
  markSyncSuccessNow, // D-233 MEASURE_5: 同期成功時刻の記録
  rekeyPatrolRecordToReadingId, // D-234 IMPL_3: 同期成功時にローカル行を reading_id へ付け替え
} from '../lib/localStore/patrolRecords'
import { invalidatePrevHold } from './patrolPrevHold' // D-235: 同期成功で tier-0 hold 無効化
import { logger } from '../lib/logger'
import { Sentry } from '../lib/sentry'
import { isOfflineContinuation } from '../lib/auth/offlineGate' // D-304: 圏外継続中は送信を skip(failed にしない)

const LOG_ERR_UPLOAD = 'ERR-LF1-SYNC-UPLOAD'
const LOG_ERR_PROBE  = 'ERR-LF1-SYNC-PROBE'
const LOG_OFFLINE_CONT = 'OFFLINE_CONTINUATION_SKIP'

// DIAG-PATROL-SWIPE-METER-LOST-01 (D-231) MEASURE_3: 同期失敗の実態(未送信バッジが消えない件)を計測。
//   HYPOTHESIS_B の検証材料 = 既存行に out_meter_2 があり patch 側 stock_2 が null で CHECK 違反 → UPDATE 失敗 →
//   markRecordSynced 未達で未送信のまま、を疑う。値そのもの(景品名等)は送らずキー名と null フラグのみ。
//   同一 booth_code+kind は 1 セッション 1 回に dedup。計測失敗は同期を壊さない(try/catch)。
const _d231SyncSent = new Set()
export function _resetD231SyncDedup() { _d231SyncSent.clear() }
function reportD231SyncFailed(r, { errCode, message }) {
  try {
    const key = `${r.booth_code}|sync-failed`
    if (_d231SyncSent.has(key)) return
    _d231SyncSent.add(key)
    const patch = r.optionalPatch ?? {}
    Sentry.captureMessage('D-231 sync-failed', {
      level: 'warning',
      tags: { diag: 'D-231', kind: 'sync-failed' },
      extra: {
        booth_code: r.booth_code,
        patrol_date: r.patrol_date,
        errCode: errCode ?? null,
        message: String(message ?? '').slice(0, 120),
        patch_keys: Object.keys(patch), // キー名のみ。値は送らない。
        has_out_meter_2: patch.out_meter_2 != null,
        stock_2_is_null: patch.stock_2 == null,
        stock_3_is_null: patch.stock_3 == null,
      },
    })
  } catch { /* 計測失敗は同期を壊さない */ }
}

// Per-storeCode in-flight lock: prevents concurrent calls from double-uploading the same unsynced records.
// When 2 callers race (unmount autosync + manual button + Hub autosync), the second returns the same
// in-flight Promise rather than starting a duplicate run. Deleted in finally so subsequent calls start fresh.
const inFlightByStore = new Map()
export function _resetInFlight() { inFlightByStore.clear() }

// SPEC-LF1-PROBE-SUPABASE-DIRECT-01: 軽量 connectivity probe。navigator.onLine は嘘をつく
// ことがあるので Supabase 本体の到達性を直接確認する。公式の軽量エンドポイント GoTrue の
// GET {url}/auth/v1/health (apikey 必須) を叩く — テーブル非依存で cheap な 200 を返す
// (実測 107B, {"name":"GoTrue","version":...})。url/apikey は supabase client instance の
// 解決済み値を使う (SPEC-HOTFIX-APIKEY-FALLBACK-01 の JWT フォールバック済みキーを尊重)。
//
// 旧実装は same-origin '/version.json' を叩いていたため、develop preview の Vercel
// Deployment Protection が 302→vercel.com SSO を返し probe が false になって全同期が沈黙
// する事故を起こした (根本原因 = probe 先が Supabase でなくアプリ自身の origin だったこと)。
// fetch 失敗 / 非2xx は offline 扱い。
export async function probeOnline({ fetcher = globalThis.fetch, signal } = {}) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false
  if (!fetcher) return true // SSR / test: 信用する
  try {
    const base = String(supabase?.supabaseUrl ?? '').replace(/\/+$/, '')
    const apikey = supabase?.supabaseKey
    const res = await fetcher(`${base}/auth/v1/health`, {
      method: 'GET',
      headers: { apikey },
      cache: 'no-store',
      signal,
    })
    return !!res?.ok
  } catch (err) {
    logger.warn?.(LOG_ERR_PROBE, { message: err?.message })
    return false
  }
}

/**
 * 指定 store の未送信 record を upload。
 * - LF1 は1件ずつ savePatrolReading に渡す。失敗は次の record に進む。
 * - 完了後、(uploaded, failed) を返す。banner の集計再評価は caller 責任。
 * - 同一 storeCode への並行呼び出しは in-flight Promise を共有し二重 INSERT を防ぐ。
 */
export function uploadStoreRecords(storeCode, { staff, skipProbe = false } = {}) {
  if (!storeCode) return Promise.resolve({ uploaded: 0, failed: 0, skipped: 0 })
  if (inFlightByStore.has(storeCode)) return inFlightByStore.get(storeCode)
  const run = _runUploadStore(storeCode, { staff, skipProbe })
    .finally(() => inFlightByStore.delete(storeCode))
  inFlightByStore.set(storeCode, run)
  return run
}

async function _runUploadStore(storeCode, { staff, skipProbe = false } = {}) {
  const records = await getPatrolRecordsByStore(storeCode)
  // D-1038 (b): 端末で保存した順(createdLocally の昇順)に上げる。サーバの created_at は届いた順なので、
  //   IDB の返す順(主キー=ランダム UUID の順)のままだと同じ日の 入替→巡回 が逆に並び、入替がその日の最後の行になる。
  //   createdLocally が無い行は先頭へ(古い端末の行。従来どおり送る)。sort は安定なので同時刻は従来の順のまま。
  const unsynced = records.filter(r => !r.synced)
    .sort((a, b) => String(a.createdLocally ?? '').localeCompare(String(b.createdLocally ?? '')))
  if (!unsynced.length) return { uploaded: 0, failed: 0, skipped: 0 }

  // D-304 IMPL_5/判断6: 圏外継続中は送信せず skip。failed にしない・setRecordLastErrCode を呼ばない・
  //   IDB の synced フラグにも触れない(IDB に残る限りデータは失われていない)。
  if (isOfflineContinuation()) {
    logger.warn?.(LOG_OFFLINE_CONT, { storeCode, skipped: unsynced.length })
    return { uploaded: 0, failed: 0, skipped: unsynced.length }
  }

  if (!skipProbe) {
    const ok = await probeOnline()
    if (!ok) {
      logger.warn?.(LOG_ERR_PROBE, { storeCode, skipped: unsynced.length })
      return { uploaded: 0, failed: 0, skipped: unsynced.length }
    }
  }

  let uploaded = 0
  let failed = 0
  for (const r of unsynced) {
    try {
      const res = await savePatrolReading({
        boothCode: r.booth_code,
        storeCode: r.store_code,
        machineCode: r.machine_code,
        inMeter: r.in_meter,
        outMeter: r.out_meter,
        prizeStock: r.prize_stock_count,
        prizeRestock: r.prize_restock_count,
        entryType: r.entry_type ?? 'patrol',
        staffId: staff?.staffId ?? r.created_by ?? null,
        optionalPatch: r.optionalPatch ?? {},
        defaultsFromPrev: r.defaultsFromPrev ?? null,
        // SPEC-LF1-IDEMPOTENT-SYNC-01 D1/D2/D3: replay は queued record 自身の値を渡す。
        patrolDate: r.patrol_date,        // D1: D 日作成→D+k replay でも D 日行になる
        readingId: r.localId,             // D2: 冪等キー。23505 は ok:true skipped:duplicate
        clientTimestamp: r.createdLocally, // D3: 新しい手動入力を古い queued で上書きしない
      })
      if (res?.ok) {
        // ok:true は inserted / updated / skipped(duplicate|stale) 全て「配信済み」→ synced
        await markRecordSynced(r.localId)
        markSyncSuccessNow() // D-233 MEASURE_5: 同期成功時刻を記録(挙動は変えない)
        // D-234 IMPL_3: サーバの reading_id へローカル行を付け替え(同一 ID 化で二重行/掃除消失を根治)。
        //   失敗しても同期は成功扱いのまま。例外は握って logger に残す(uploaded/エラー分岐は不変)。
        try {
          // D-808 AC4: サーバに確定した optional も渡し、ローカル行をサーバ行と同じ形にする。
          await rekeyPatrolRecordToReadingId(r.localId, res.readingId, res.savedOptional ?? null)
        } catch (rekeyErr) {
          logger.error?.(LOG_ERR_UPLOAD, { localId: r.localId, phase: 'rekey', message: rekeyErr?.message })
        }
        // D-235: 同期成功で当該行が synced 化し tier-1 から外れる。古い tier-0 hold が当たり続けるのを防ぐため
        //   当該ブースの hold を無効化(次に開くと tier-2=端末 DB の正しい行へ落ちる)。無効化失敗でも同期は成功扱い。
        try {
          invalidatePrevHold(r.booth_code)
        } catch (invErr) {
          logger.error?.(LOG_ERR_UPLOAD, { localId: r.localId, phase: 'prev-hold-invalidate', message: invErr?.message })
        }
        // SPEC-METER-DELTA-ANOMALY-CHECK-01 (D-357): 保存フロー末尾でメーター差分異常を事後検知(fire-and-forget)。
        //   実際に値が着地した inserted/updated のみ対象(duplicate/stale/unchanged は再検知しない)。
        //   ★await しない=検知の成否に関わらず同期を絶対にブロック・遅延・失敗させない(現場データを失わない)。
        if (res.inserted || res.updated) void flagMeterDeltaAnomaly(r.booth_code, res.readingId)
        // D-410 ITEM_3: 払出メーター異常も同じ引数で fire-and-forget(保存の成否に影響させない)。
        if (res.inserted || res.updated) void flagPrizeOutAnomaly(r.booth_code, res.readingId)
        uploaded++
      } else {
        failed++
        // D5: 失敗は Sentry (logger.error) へ。server 不可視な同期失敗を可視化する。
        logger.error?.(LOG_ERR_UPLOAD, {
          localId: r.localId, booth_code: r.booth_code, patrol_date: r.patrol_date,
          errCode: res?.errCode, message: res?.message,
        })
        reportD231SyncFailed(r, { errCode: res?.errCode, message: res?.message }) // D-231 MEASURE_3
        await setRecordLastErrCode(r.localId, res?.errCode ?? 'unknown').catch(() => {})
      }
    } catch (err) {
      failed++
      logger.error?.(LOG_ERR_UPLOAD, {
        localId: r.localId, booth_code: r.booth_code, patrol_date: r.patrol_date, message: err?.message,
      })
      reportD231SyncFailed(r, { errCode: 'exception', message: err?.message }) // D-231 MEASURE_3
      await setRecordLastErrCode(r.localId, 'exception').catch(() => {})
    }
  }
  // D5: 失敗ありのサマリは logger.error (Sentry)、全成功は info。
  const doneCtx = { storeCode, uploaded, failed, skipped: 0 }
  if (failed > 0) logger.error?.('LF1_UPLOAD_DONE', doneCtx)
  else logger.info?.('LF1_UPLOAD_DONE', doneCtx)
  return { uploaded, failed, skipped: 0 }
}

// 全 store 横断版 (autologout 時 / business-end ボタン用、LF1 は 内部 only の便宜)
export async function uploadAllUnsynced({ staff } = {}) {
  const all = await getUnsyncedRecords()
  if (!all.length) return { uploaded: 0, failed: 0, skipped: 0 }
  const byStore = new Map()
  for (let r of all) {
    // FIX3: store_code:null レコードを booth_code 逆引きで補完してから送信試行。
    // IDB record も補完更新して以降の getPatrolRecordsByStore(storeCode) で取れるようにする。
    // 逆引き不能なら skip 継続 (FIX4 sweep の対象)。
    if (!r.store_code) {
      const derived = _deriveStoreCode(r.booth_code)
      if (!derived) continue
      r = { ...r, store_code: derived }
      await putPatrolRecord(r).catch(() => {})
    }
    if (!byStore.has(r.store_code)) byStore.set(r.store_code, [])
    byStore.get(r.store_code).push(r)
  }
  let uploaded = 0, failed = 0, skipped = 0
  for (const storeCode of byStore.keys()) {
    const res = await uploadStoreRecords(storeCode, { staff })
    uploaded += res.uploaded
    failed   += res.failed
    skipped  += res.skipped
  }
  return { uploaded, failed, skipped }
}

// SPEC-LF1-IDEMPOTENT-SYNC-01 D6: shared trailing-debounce trigger for the app-level
// 'online' + visibilitychange listeners. A burst of events collapses to ONE
// uploadAllUnsynced after debounceMs of quiet. fire-and-forget (never rejects to the caller).
export function makeDebouncedUploadAll({ getStaff, debounceMs = 10000, uploader = uploadAllUnsynced } = {}) {
  let timer = null
  return function trigger() {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      timer = null
      Promise.resolve(uploader({ staff: getStaff?.() })).catch(() => {})
    }, debounceMs)
  }
}
